import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { calculateMeetingCost } from './calculateMeetingCost';
import { meetingCostService } from '../../services/MeetingCostService';
import { getRolesByRegion } from '../../data/roles';
import { formatCurrency } from '../../utils/formatCurrency';
import { useCurrency } from '../../contexts/CurrencyContext';
import { getRandomInstruction } from '../../utils/instructions';
import AttendeePicker from '../../components/AttendeePicker';
import ShareButton from '../../components/ShareButton';
import { compactRainbowShareButtonStyles } from '../../theme/shareButtonStyles';

export default function MeetingForm() {
  const theme = useTheme();
  const { currency, region } = useCurrency();
  const roles = useMemo(() => getRolesByRegion(region), [region]);
  const [counts, setCounts] = useState(
    roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {})
  );
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [prepTimeMinutes, setPrepTimeMinutes] = useState(30);
  const [recurrence, setRecurrence] = useState('none');
  const [result, setResult] = useState(null);
  const [recurringResult, setRecurringResult] = useState(null);
  const [displayTotal, setDisplayTotal] = useState(0);
  const [instruction] = useState(getRandomInstruction());
  const resultsRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const [hasScrolledToResults, setHasScrolledToResults] = useState(false);

  // Update counts when region changes to ensure all role keys are present, preserving existing values
  useEffect(() => {
    setCounts(prev => {
      const newCounts = {};
      let hasChanges = false;
      
      // Add all role keys, preserving existing counts
      roles.forEach(role => {
        newCounts[role.key] = prev[role.key] || 0;
      });
      
      // Check if we need to update - only if keys are different
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(newCounts);
      
      if (prevKeys.length !== newKeys.length || 
          !prevKeys.every(key => newKeys.includes(key))) {
        hasChanges = true;
      }
      
      // Only update if there are actual changes
      return hasChanges ? newCounts : prev;
    });
}, [region, roles]);

  const handleChange = (roleKey, value) => {
    setCounts((prev) => ({
      ...prev,
      [roleKey]: value,
    }));
  };

  // Calculate cost automatically when inputs change
  useEffect(() => {
    const attendees = roles.map((role) => ({
      role: role.label,
      count: counts[role.key],
      hourlyRate: role.hourlyRate,
    }));

    const totalAttendees = attendees.reduce((sum, a) => sum + a.count, 0);
    
    if (totalAttendees > 0) {
      const input = {
        attendees,
        durationMinutes: Number(durationMinutes) || 0,
        prepTimeMinutes: Number(prepTimeMinutes) || 0,
      };

      const output = calculateMeetingCost(input);
      setResult(output);
      setDisplayTotal(output.totalCost);

      // Calculate recurring cost if recurrence is selected
      if (recurrence !== 'none') {
        const recurringOutput = meetingCostService.calculateRecurringCost({
          ...input,
          recurrence
        });
        setRecurringResult(recurringOutput);
      } else {
        setRecurringResult(null);
      }

      // Auto-scroll to results after user stops making changes (debounced)
      if (output.totalCost > 0 && !hasScrolledToResults) {
        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        // Set new timeout - scroll after user stops making changes for 1.5 seconds
        scrollTimeoutRef.current = setTimeout(() => {
          if (resultsRef.current) {
            resultsRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
            setHasScrolledToResults(true);
          }
        }, 1500);
      }
    } else {
      setResult(null);
      setRecurringResult(null);
      setDisplayTotal(0);
    }
}, [counts, durationMinutes, prepTimeMinutes, recurrence, hasScrolledToResults, region, roles]);

  const handleReset = () => {
    // Clear any pending scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    setCounts(roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {}));
    setDurationMinutes(60);
    setPrepTimeMinutes(30);
    setRecurrence('none');
    setResult(null);
    setRecurringResult(null);
    setDisplayTotal(0);
    setHasScrolledToResults(false);
  };

  return (
  <Paper elevation={3} sx={{ p: 3 }}>
  <Typography
    variant="h4"
    gutterBottom
    sx={{
      mb: 4,
      textAlign: 'center',
      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
      fontWeight: 700,}}
  >
    Meeting Cost Calculator
  </Typography>

  <Typography
  variant="subtitle1"
  align="center"
  sx={{
    mb: 4,
    color: 'text.secondary',
    fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
 }}
  >
    {instruction}
  </Typography>

  <Grid container rowSpacing={3}>
    {/* Section: Meeting Details */}
    <Grid size={{ xs: 12}}>
      <Box sx={{ p: 2.5, backgroundColor: 'action.hover', borderRadius: 2 }}>
        <Grid container columnSpacing={3} rowSpacing={2.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Meeting Duration (minutes)"
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Prep Time per Attendee (minutes)"
              type="number"
              value={prepTimeMinutes}
              onChange={(e) => setPrepTimeMinutes(Number(e.target.value) || 0)}
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Recurrence</InputLabel>
              <Select
                value={recurrence}
                label="Recurrence"
                onChange={(e) => setRecurrence(e.target.value)}
              >
                <MenuItem value="none">One-time meeting</MenuItem>
                <MenuItem value="daily">Daily (5 days/week)</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="fortnightly">Fortnightly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Grid>

    {/* Section: Attendees */}
    <Grid size={{ xs: 12}}>
      <Box sx={{ p: 2.5, backgroundColor: 'action.hover', borderRadius: 2 }}>
        <Grid container columnSpacing={3} rowSpacing={2.5}>
          {roles.map((role) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.key}>
              <AttendeePicker
                label={role.label}
                value={counts[role.key]}
                onChange={(value) => handleChange(role.key, value)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Grid>

  </Grid>

  {/* Visual hint when no results yet */}
  {(!result || displayTotal === 0) && (
    <Box 
      sx={{ 
        textAlign: 'center', 
        mt: 3, 
        py: 2,
        opacity: 0.6,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        👆 Add attendees above to see cost results below 👇
      </Typography>
    </Box>
  )}

  {/* Consolidated Results Section - Now below inputs */}
  {result && displayTotal > 0 && (
    <Box
      ref={resultsRef}
      mt={3}
      p={3}
      sx={{
        borderRadius: 4,
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
          : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
        border: theme.palette.mode === 'dark' 
          ? '2px solid rgba(255,255,255,0.1)'
          : '2px solid rgba(25, 118, 210, 0.12)',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '180px',
      }}
    >
      <Button
        variant="contained"
        size="small"
        onClick={handleReset}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          fontSize: '0.8rem',
          minWidth: 'auto',
          px: 2,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: '#ff6b6b',
          color: 'white',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#ff5252',
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(255, 107, 107, 0.4)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        Reset
      </Button>
      
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary',
          mb: 2,
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1,
          textAlign: 'center',
        }}
      >
        📊 Meeting Cost Results
      </Typography>
      
      {/* Two-column cost display */}
      <Grid container spacing={2} sx={{ mb: 2, transition: 'all 0.3s ease-in-out' }}>
        <Grid size={{ xs: 12, sm: recurringResult && recurrence !== 'none' ? 6 : 12 }}>
          <Box sx={{ textAlign: 'center', transition: 'all 0.3s ease-in-out' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                mb: 1,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 600,
              }}
            >
              {recurringResult && recurrence !== 'none' ? 'Single Meeting' : 'Total Cost'}
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                mb: 0.5,
                fontFamily: 'monospace',
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                letterSpacing: 1,
              }}
            >
              {formatCurrency(displayTotal, currency)}
            </Typography>
            {recurringResult && recurrence !== 'none' && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                }}
              >
                per meeting
              </Typography>
            )}
          </Box>
        </Grid>
        
        {recurringResult && recurrence !== 'none' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ textAlign: 'center', transition: 'all 0.3s ease-in-out' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                Yearly Total
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  color: theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
                  mb: 0.5,
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                  letterSpacing: 1,
                }}
              >
                {formatCurrency(recurringResult.yearlyTotalCost, currency)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                }}
              >
                {recurringResult.recurrenceDetails.occurrencesPerYear} meetings
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      
      {/* Single snarky quip */}
      <Box sx={{ minHeight: '28px', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            color: 'text.secondary',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          {recurringResult && recurrence !== 'none' ? recurringResult.yearlySnarkyQuip : result.snarkyQuip}
        </Typography>
      </Box>
      
      {/* Integrated Share Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ShareButton
          meetingName="Meeting Cost Calculation"
          attendees={counts}
          elapsedSeconds={durationMinutes * 60}
          finalCost={displayTotal}
          recurringData={recurringResult ? {
            frequency: recurrence,
            occurrencesPerYear: recurringResult.recurrenceDetails.occurrencesPerYear,
            yearlyTotalCost: recurringResult.yearlyTotalCost,
            yearlySnarkyQuip: recurringResult.yearlySnarkyQuip
          } : null}
          sourceCalculator="meeting"
          variant="contained"
          size="medium"
          sx={compactRainbowShareButtonStyles}
        />
      </Box>
    </Box>
  )}
</Paper>


  );
}
